// Helper function to create OpenAI thread
export async function createOpenAIThread(headers: Record<string, string>): Promise<string> {
  const threadRes = await fetch('https://api.openai.com/v1/threads', {
    method: 'POST',
    headers: headers
  })
  
  if (!threadRes.ok) {
    const errorText = await threadRes.text()
    throw new Error(`Failed to create thread: ${threadRes.statusText} - ${errorText}`)
  }
  
  const threadData = await threadRes.json()
  return threadData.id
}

// Helper function to send message to thread
export async function sendMessageToThread(
  threadId: string,
  message: string,
  headers: Record<string, string>
): Promise<void> {
  const messageRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/messages`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      role: 'user',
      content: message
    })
  })
  
  if (!messageRes.ok) {
    const errorText = await messageRes.text()
    throw new Error(`Failed to send message: ${messageRes.statusText} - ${errorText}`)
  }
}

// Helper function to run OpenAI assistant
export async function runOpenAIAssistant(
  threadId: string,
  assistantId: string,
  headers: Record<string, string>
): Promise<string> {
  const runRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      assistant_id: assistantId
    })
  })
  
  if (!runRes.ok) {
    const errorText = await runRes.text()
    throw new Error(`Failed to run assistant: ${runRes.statusText} - ${errorText}`)
  }
  
  const runData = await runRes.json()
  return runData.id
}

// Helper function to poll for completion
export async function pollForCompletion(
  threadId: string,
  headers: Record<string, string>
): Promise<void> {
  let runStatus = 'queued'
  const maxAttempts = 60
  let attempts = 0
  
  while (runStatus !== 'completed' && runStatus !== 'failed' && attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    const statusRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs/latest`, {
      headers: headers
    })
    
    if (!statusRes.ok) {
      throw new Error(`Failed to check run status: ${statusRes.statusText}`)
    }
    
    const statusData = await statusRes.json()
    runStatus = statusData.status
    
    attempts++
  }
  
  if (runStatus !== 'completed') {
    throw new Error(`Run did not complete: ${runStatus}`)
  }
}

// Helper function to get thread messages
export async function getThreadMessages(
  threadId: string,
  headers: Record<string, string>
): Promise<string> {
  const messagesRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/messages`, {
    headers: headers
  })
  
  if (!messagesRes.ok) {
    const errorText = await messagesRes.text()
    throw new Error(`Failed to get messages: ${messagesRes.statusText} - ${errorText}`)
  }
  
  const messagesData = await messagesRes.json()
  const lastMessage = messagesData.data[0]
  
  if (!lastMessage || !lastMessage.content || !lastMessage.content[0] || !lastMessage.content[0].text) {
    throw new Error('No message content found')
  }
  
  return lastMessage.content[0].text.value
}

// Helper function to process OpenAI analysis
export async function processOpenAIAnalysis(
  threadId: string,
  prompt: string,
  headers: Record<string, string>,
  ASSISTANT_ID: string
): Promise<string> {
  await sendMessageToThread(threadId, prompt, headers)
  await runOpenAIAssistant(threadId, ASSISTANT_ID, headers)
  await pollForCompletion(threadId, headers)
  return await getThreadMessages(threadId, headers)
}
